import React, { useState, useEffect } from 'react';
import './StudentNotes.css';
import DashboardHeader from '../components/DashboardHeader';
import ParentNavigationBar from '../components/ParentNavigationBar';
import { BookIcon, CalendarIcon } from '../components/Icons';
import { toSentenceCase } from '../utils/textUtils';

function StudentNotes() {
  const [selectedTrimester, setSelectedTrimester] = useState('3');
  const [studentInfo, setStudentInfo] = useState(null);
  const [notes, setNotes] = useState([]);
  const [stats, setStats] = useState({
    moyenne_generale: 0,
    rang: 0,
    total_notes: 0,
    notes_faibles: 0,
    meilleures_matieres: [],
    matieres_a_ameliorer: []
  });
  const [loading, setLoading] = useState(true);
  const [selectedMatiere, setSelectedMatiere] = useState('toutes');

  useEffect(() => {
    fetchStudentData();
  }, []);

  useEffect(() => {
    if (studentInfo?.id) {
      fetchNotes();
    }
  }, [selectedTrimester, studentInfo]);

  const fetchStudentData = async () => {
    try {
      const userEmail = localStorage.getItem('user_email') || 'sedjro153@gmail.com';
      const response = await fetch(`http://127.0.0.1:8000/api/v1/etudiant/me?email=${userEmail}`);
      const data = await response.json();
      
      if (response.ok && data.etudiant) {
        setStudentInfo(data.etudiant);
      }
    } catch (error) {
      console.error('❌ Erreur fetch étudiant:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      setLoading(true);
      
      // Récupérer les notes
      const notesResponse = await fetch(`http://127.0.0.1:8000/api/v1/admin/notes/etudiant/${studentInfo.id}`);
      const notesData = await notesResponse.json();
      
      if (notesResponse.ok && Array.isArray(notesData)) {
        const notesFiltered = notesData.filter(n => 
          n.trimestre === selectedTrimester && 
          n.annee_scolaire === '2025-2026'
        );
        setNotes(notesFiltered);
        
        // Calculer les statistiques
        calculateStats(notesFiltered);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('❌ Erreur récupération notes:', error);
      setLoading(false);
    }
  };

  const calculateStats = (notesData) => {
    if (notesData.length === 0) {
      setStats({
        moyenne_generale: 0,
        rang: 0,
        total_notes: 0,
        notes_faibles: 0,
        meilleures_matieres: [],
        matieres_a_ameliorer: []
      });
      return;
    }

    // Grouper par matière
    const notesByMatiere = notesData.reduce((acc, note) => {
      if (!acc[note.matiere]) {
        acc[note.matiere] = [];
      }
      acc[note.matiere].push(note);
      return acc;
    }, {});

    // Calculer moyennes par matière
    const moyennesMatiere = Object.entries(notesByMatiere).map(([matiere, notes]) => {
      const totalPoints = notes.reduce((sum, note) => sum + (note.note * (note.coefficient || 1)), 0);
      const totalCoeff = notes.reduce((sum, note) => sum + (note.coefficient || 1), 0);
      const moyenne = totalCoeff > 0 ? totalPoints / totalCoeff : 0;
      
      return {
        matiere,
        moyenne: Math.round(moyenne * 100) / 100,
        notes: notes.length,
        coefficient: totalCoeff
      };
    });

    // Moyenne générale
    const totalPoints = moyennesMatiere.reduce((sum, m) => sum + (m.moyenne * m.coefficient), 0);
    const totalCoeff = moyennesMatiere.reduce((sum, m) => sum + m.coefficient, 0);
    const moyenneGenerale = totalCoeff > 0 ? Math.round((totalPoints / totalCoeff) * 100) / 100 : 0;

    // Meilleures matières et à améliorer
    const matieresSorted = [...moyennesMatiere].sort((a, b) => b.moyenne - a.moyenne);
    const meilleures = matieresSorted.slice(0, 3);
    const aAmeliorer = matieresSorted.slice(-3).reverse();

    // Notes faibles
    const notesFaibles = notesData.filter(note => note.note < 10).length;

    setStats({
      moyenne_generale: moyenneGenerale,
      rang: 0, // À récupérer du bulletin si disponible
      total_notes: notesData.length,
      notes_faibles: notesFaibles,
      meilleures_matieres: meilleures,
      matieres_a_ameliorer: aAmeliorer.filter(m => m.moyenne < 10)
    });
  };

  const getUniqueMatières = () => {
    const matieres = [...new Set(notes.map(note => note.matiere))];
    return matieres.sort();
  };

  const getFilteredNotes = () => {
    if (selectedMatiere === 'toutes') {
      return notes;
    }
    return notes.filter(note => note.matiere === selectedMatiere);
  };

  const getNoteColor = (note) => {
    if (note >= 16) return '#27AE60'; // Vert
    if (note >= 14) return '#3498DB'; // Bleu
    if (note >= 12) return '#F39C12'; // Orange
    if (note >= 10) return '#E67E22'; // Orange foncé
    return '#E74C3C'; // Rouge
  };

  const getNoteLabel = (note) => {
    if (note >= 16) return 'Excellent';
    if (note >= 14) return 'Très bien';
    if (note >= 12) return 'Bien';
    if (note >= 10) return 'Assez bien';
    return 'Insuffisant';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'devoir': return '📝';
      case 'composition': return '📋';
      case 'examen': return '🎯';
      default: return '📄';
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <ParentNavigationBar 
          title="Mes Notes" 
          onBack={() => window.navigateTo && window.navigateTo('student-dashboard')}
        />
        <div className="admin-container">
          <main className="student-notes-main admin-content-full">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Chargement de vos notes...</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <ParentNavigationBar 
        title="Mes Notes" 
        onBack={() => window.navigateTo && window.navigateTo('student-dashboard')}
      />
      <div className="admin-container">
        <main className="student-notes-main admin-content-full">
          <div className="student-notes-container">
            
            {/* En-tête avec informations étudiant */}
            <div className="notes-header">
              <div className="student-info-card">
                <div className="student-avatar">
                  {studentInfo?.prenom?.charAt(0)}{studentInfo?.nom?.charAt(0)}
                </div>
                <div className="student-details">
                  <h2>{studentInfo?.prenom} {studentInfo?.nom}</h2>
                  <p className="student-class">Classe: {studentInfo?.classe} • Année: 2025-2026</p>
                </div>
              </div>
              
              <div className="trimester-selector-card">
                <label>📅 Trimestre:</label>
                <select 
                  value={selectedTrimester} 
                  onChange={(e) => setSelectedTrimester(e.target.value)}
                >
                  <option value="1">Trimestre 1</option>
                  <option value="2">Trimestre 2</option>
                  <option value="3">Trimestre 3</option>
                </select>
              </div>
            </div>

            {/* Statistiques générales */}
            <div className="notes-stats-grid">
              <div className="stat-card-notes primary">
                <div className="stat-icon">📊</div>
                <div className="stat-content">
                  <h3>{stats.moyenne_generale}/20</h3>
                  <p>Moyenne générale</p>
                  <span className={`trend ${stats.moyenne_generale >= 10 ? 'positive' : 'negative'}`}>
                    {stats.moyenne_generale >= 10 ? '📈' : '📉'}
                    {stats.moyenne_generale >= 10 ? 'Satisfaisant' : 'À améliorer'}
                  </span>
                </div>
              </div>
              
              <div className="stat-card-notes">
                <div className="stat-icon">📝</div>
                <div className="stat-content">
                  <h3>{stats.total_notes}</h3>
                  <p>Total des notes</p>
                </div>
              </div>
              
              <div className="stat-card-notes warning">
                <div className="stat-icon">⚠️</div>
                <div className="stat-content">
                  <h3>{stats.notes_faibles}</h3>
                  <p>Notes inférieures à 10/20</p>
                </div>
              </div>
              
              <div className="stat-card-notes success">
                <div className="stat-icon">🏆</div>
                <div className="stat-content">
                  <h3>{stats.meilleures_matieres.length}</h3>
                  <p>Matières fortes</p>
                </div>
              </div>
            </div>

            {/* Meilleures matières et à améliorer */}
            <div className="performance-overview">
              <div className="performance-card success">
                <h3>🏆 Meilleures matières</h3>
                {stats.meilleures_matieres.length > 0 ? (
                  <div className="performance-list">
                    {stats.meilleures_matieres.map((matiere, index) => (
                      <div key={index} className="performance-item">
                        <span className="matiere-name">{matiere.matiere}</span>
                        <span className="matiere-moyenne">{matiere.moyenne}/20</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">Aucune donnée disponible</p>
                )}
              </div>
              
              <div className="performance-card warning">
                <h3>📈 À améliorer</h3>
                {stats.matieres_a_ameliorer.length > 0 ? (
                  <div className="performance-list">
                    {stats.matieres_a_ameliorer.map((matiere, index) => (
                      <div key={index} className="performance-item">
                        <span className="matiere-name">{matiere.matiere}</span>
                        <span className="matiere-moyenne">{matiere.moyenne}/20</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="no-data">Toutes les matières sont satisfaisantes ✅</p>
                )}
              </div>
            </div>

            {/* Filtres */}
            <div className="notes-filters">
              <div className="filter-group">
                <label>📚 Matière:</label>
                <select 
                  value={selectedMatiere} 
                  onChange={(e) => setSelectedMatiere(e.target.value)}
                >
                  <option value="toutes">Toutes les matières</option>
                  {getUniqueMatières().map(matiere => (
                    <option key={matiere} value={matiere}>{matiere}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Liste des notes */}
            <div className="notes-list-container">
              <div className="notes-list-header">
                <h3>📋 Détail des notes - Trimestre {selectedTrimester}</h3>
                <span className="notes-count">{getFilteredNotes().length} note(s)</span>
              </div>
              
              {getFilteredNotes().length === 0 ? (
                <div className="empty-notes">
                  <div className="empty-icon">📝</div>
                  <h4>Aucune note disponible</h4>
                  <p>Aucune note n'a été saisie pour ce trimestre.</p>
                </div>
              ) : (
                <div className="notes-grid">
                  {getFilteredNotes().map(note => (
                    <div key={note.id} className="note-card">
                      <div className="note-header">
                        <div className="note-matiere">
                          <span className="matiere-name">{note.matiere}</span>
                          <span className="note-type">
                            {getTypeIcon(note.type)} {toSentenceCase(note.type)}
                          </span>
                        </div>
                        <div 
                          className="note-value"
                          style={{ backgroundColor: getNoteColor(note.note) }}
                        >
                          {note.note}/20
                        </div>
                      </div>
                      
                      <div className="note-details">
                        <div className="note-info">
                          <span className="note-coeff">Coefficient: {note.coefficient || 1}</span>
                          <span className="note-date">
                            <CalendarIcon size={14} />
                            {new Date(note.created_at).toLocaleDateString('fr-FR')}
                          </span>
                        </div>
                        
                        <div className="note-appreciation">
                          <span className={`note-label ${note.note < 10 ? 'insufficient' : note.note >= 16 ? 'excellent' : 'good'}`}>
                            {getNoteLabel(note.note)}
                          </span>
                        </div>
                      </div>
                      
                      {note.observation && (
                        <div className="note-observation">
                          <p>💬 {note.observation}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Conseils et encouragements */}
            <div className="notes-advice">
              <div className="advice-card">
                <h3>💡 Conseils pour progresser</h3>
                <div className="advice-content">
                  {stats.moyenne_generale >= 16 ? (
                    <p>🎉 Excellent travail ! Continuez sur cette voie et maintenez vos efforts.</p>
                  ) : stats.moyenne_generale >= 14 ? (
                    <p>👏 Très bon niveau ! Quelques efforts supplémentaires vous permettront d'atteindre l'excellence.</p>
                  ) : stats.moyenne_generale >= 12 ? (
                    <p>👍 Bon travail ! Concentrez-vous sur les matières à améliorer pour progresser.</p>
                  ) : stats.moyenne_generale >= 10 ? (
                    <p>📚 Travail satisfaisant. Identifiez vos difficultés et n'hésitez pas à demander de l'aide.</p>
                  ) : (
                    <p>💪 Des efforts supplémentaires sont nécessaires. Parlez avec vos professeurs et organisez mieux votre travail.</p>
                  )}
                  
                  {stats.notes_faibles > 0 && (
                    <p className="warning-advice">
                      ⚠️ Vous avez {stats.notes_faibles} note(s) en dessous de 10/20. 
                      Concentrez-vous sur ces matières et demandez de l'aide si nécessaire.
                    </p>
                  )}
                </div>
              </div>
            </div>

          </div>
        </main>
      </div>
    </div>
  );
}

export default StudentNotes;